import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Alert } from '@neo/primitives/alert'
import { Button } from '@neo/primitives/button'
import { Card } from '@neo/primitives/card'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, Navigate, useSearchParams } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { z } from 'zod'
import { AuthLayout } from '../../shared/layouts/auth'
import {
  AUTH_INPUT_CLASS,
  AUTH_LINK_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
} from './components/auth-styles'
import { useResetPasswordMutationV2, useValidateResetTokenMutationV2 } from './queries'
import { validatePassword } from './utils'

interface ResetPasswordFormData {
  password: string
  confirmPassword: string
}

const fields: GenericFieldConfig<ResetPasswordFormData> = [
  {
    name: 'password',
    componentType: 'inputPassword',
    zodValidation: () => validatePassword(),
    label: 'New Password',
    errorClassName: 'error-list-format',
    inputProps: {
      placeholder: 'Enter your new password',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'new-password',
    },
  },
  {
    name: 'confirmPassword',
    componentType: 'inputPassword',
    onChangeListenTo: ['password'],
    getDynamicProps: (values) => ({
      zodVal: z
        .string()
        .min(1, 'Please confirm your password')
        .refine((val) => val === values.password, {
          message: 'Passwords do not match',
        }),
    }),
    label: 'Confirm Password',
    inputProps: {
      placeholder: 'Enter your new password again',
      inputClassName: AUTH_INPUT_CLASS,
      autoComplete: 'new-password',
    },
  },
]

export const ResetPasswordV2 = () => {
  const { userAuthenticated } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const recaptchaRef = useRef() as React.MutableRefObject<ReCAPTCHA>
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null)
  const [resetSuccess, setResetSuccess] = useState(false)

  const { mutate: validateToken } = useValidateResetTokenMutationV2()
  const { isPending, mutate: resetPassword, error } = useResetPasswordMutationV2(recaptchaRef)

  const defaultValueRef = useRef<ResetPasswordFormData>({
    password: '',
    confirmPassword: '',
  })

  useEffect(() => {
    if (token) {
      validateToken(
        { token },
        {
          onSuccess: () => setIsTokenValid(true),
          onError: () => setIsTokenValid(false),
        },
      )
    } else {
      setIsTokenValid(false)
    }
  }, [token, validateToken])

  if (userAuthenticated) return <Navigate to={getDefaultRoute()} />

  if (!token || isTokenValid === false) {
    return (
      <AuthLayout
        pageTitle="Reset password"
        title="Invalid Reset Link"
        description="This password reset link is invalid or has expired."
      >
        <Card className="p-6 text-center">
          <Button asChild typography="action" size="xl" className={AUTH_PRIMARY_BUTTON_CLASS}>
            <Link to="/forgot-password">Request New Reset Link</Link>
          </Button>
        </Card>
      </AuthLayout>
    )
  }

  if (resetSuccess) {
    return (
      <AuthLayout
        pageTitle="Reset password"
        title="Password Reset Successfully"
        description="Your password has been reset successfully. Please login with your new password."
        footer={
          <div className="text-center text-p-400 text-secondary">
            Ready to login?{' '}
            <Link to="/login" className={AUTH_LINK_CLASS}>
              Login
            </Link>
          </div>
        }
      >
        <Card className="p-6 text-center">
          <Button asChild typography="action" size="xl" className={AUTH_PRIMARY_BUTTON_CLASS}>
            <Link to="/login">Login</Link>
          </Button>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      pageTitle="Reset password"
      title="Reset your Password"
      description="Set a unique and secure password to recover your account."
      footer={
        <div className="text-center text-p-400 text-secondary">
          Remember your password?{' '}
          <Link to="/login" className={AUTH_LINK_CLASS}>
            Login
          </Link>
        </div>
      }
    >
      {error && <Alert variant="destructive" content={getErrorText(error)} />}
      <GenericForm
        id="reset-password-v2-form"
        defaultValues={defaultValueRef.current}
        fields={fields}
        onSubmit={(values) => {
          resetPassword(
            { password: values.password, token },
            { onSuccess: () => setResetSuccess(true) },
          )
        }}
        className="grid gap-4"
        isSubmitting={isPending}
        submitText="Reset Password"
        includeSubmitButton={false}
      />
      <FormSubmitButton
        formId="reset-password-v2-form"
        typography="action"
        className={AUTH_PRIMARY_BUTTON_CLASS}
        size="xl"
        loading={isPending}
      >
        Reset Password
      </FormSubmitButton>
      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
    </AuthLayout>
  )
}
